<template>
  <div class="container-fluid mt-3">
    <div class="d-flex align-items-center gap-3 mb-3">
      <button class="btn btn-secondary" @click="$router.back()">
        ⬅️ Regresar
      </button>
      <h3 class="mb-0 align-middle titulo-cuentas-pedido">Cuentas del Pedido #{{ idPedido }}</h3>
    </div>

    <div v-if="cuentas.length === 0" class="alert alert-secondary">
      No hay cuentas registradas para este pedido.
    </div>

    <div class="row g-3">
      <div v-for="cuenta in cuentas" :key="cuenta.IdCuentaPedido" class="col-12">
        <div class="card pedido-card-horizontal shadow-sm mb-4 h-100">
          <!-- Encabezado -->
          <div class="card-header d-flex justify-content-between align-items-center bg-light flex-wrap">
            <strong>{{ cuenta.NombreCuenta }}</strong>
            <span class="badge bg-secondary mt-2 mt-md-0"></span>
          </div>

          <!-- Detalles + Totales en fila (responsive) -->
          <div class="card-body row gx-3 gy-3 align-items-stretch">
            <!-- Detalles -->
            <div class="col-12 col-md-8">
              <ul class="list-group list-group-flush">
                <li
                  v-for="(det, i) in cuenta.detalles"
                  :key="i"
                  class="list-group-item d-flex justify-content-between align-items-center px-0"
                >
                  <div>
                    <strong>{{ det.nombre_platillo }}</strong>
                    <small class="d-block text-muted">
                      Cant: {{ det.Cantidad}} × Q{{ Number(det.PrecioUnitario).toFixed(2) }}
                    </small>
                    <small
                      v-for="(op, j) in det.opciones"
                      :key="j"
                      class="d-block text-muted"
                      style="padding-left: 8px;"
                    >
                      + {{ op.nombre_opcion || op.nombre_grupo || 'Extra' }}
                      <span v-if="op.precio_extra > 0">: Q{{ Number(op.precio_extra).toFixed(2) }}</span>
                    </small>
                  </div>
                  <span class="badge cantidad-badge">Q{{ (Number(det.Subtotal) + (det.opciones || []).reduce((s, op) => s + Number(op.precio_extra || 0) * det.Cantidad, 0)).toFixed(2) }}</span>
                </li>
              </ul>
            </div>

            <!-- Totales -->
            <div class="col-12 col-md-4 d-flex flex-column align-items-end justify-content-between mt-3 mt-md-0">
              <div class="totales-box w-100">
                <div class="total-item mb-2">
                  <div class="label-subtotal">Subtotal:</div>
                  <div class="valor-subtotal">Q{{ Number(cuenta.Subtotal).toFixed(2) }}</div>
                </div>
                <div class="total-item mb-2">
                  <div class="label-propina">Propina:</div>
                  <div class="valor-propina">Q{{ Number(cuenta.Propina || 0).toFixed(2) }}</div>
                </div>
                <div class="total-item mb-2">
                  <div class="label-total">Total:</div>
                  <div class="valor-total">
                    Q{{ Number(cuenta.Total ?? (Number(cuenta.Subtotal) + Number(cuenta.Propina || 0))).toFixed(2) }}
                  </div>
                </div>
                <div class="mt-3 text-end">
                  <button
                    class="btn btn-primary btn-sm"
                    @click="imprimirCuenta(cuenta.IdCuentaPedido)"
                  >
                    🖨️ Imprimir
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { API_URL } from "@/config/config";
import { useRoute } from "vue-router";
import { imprimirComandaCaja } from "@/utils/impresion";

export default {
  name: "CuentasPedidoDetalles",
  data() {
    return {
      cuentas: [],
      idPedido: null,
      pedidoInfo: null,
    };
  },
  methods: {
    async cargarCuentas() {
      try {
        const { data } = await axios.get(`${API_URL}/detalle-cuentas/${this.idPedido}`);
        this.cuentas = data;
      } catch (err) {
        console.error("Error cargando cuentas:", err);
      }
    },
    async cargarPedidoInfo() {
      try {
        const { data } = await axios.get(`${API_URL}/pedido/${this.idPedido}/info`);
        this.pedidoInfo = data;
      } catch (err) {
        console.error("Error cargando info pedido:", err);
      }
    },
    async imprimirCuenta(idCuenta) {
      try {
        // Si pedidoInfo aún no cargó (race condition al abrir rápido), esperarlo
        if (!this.pedidoInfo) {
          await this.cargarPedidoInfo();
        }

        // Recargar cuentas actualizadas
        const { data } = await axios.get(`${API_URL}/detalle-cuentas/${this.idPedido}`);
        this.cuentas = data;
        const cuenta = this.cuentas.find(c => c.IdCuentaPedido === idCuenta);
        if (!cuenta) return;

        // Obtener correlativo registrado para este pedido; si no existe, crearlo
        let correlativo = null;
        try {
          const { data: impData } = await axios.get(`${API_URL}/impresion-comanda/por-pedido/${this.idPedido}`);
          correlativo = impData.correlativo;
        } catch (e) { /* continuar */ }

        if (!correlativo) {
          try {
            const { data: regData } = await axios.post(`${API_URL}/impresion-comanda`, {
              IdPedido: this.idPedido,
              TipoComanda: 'cocina',
              FechaCreacionPedido: new Date().toISOString().slice(0, 19).replace('T', ' '),
            });
            correlativo = regData.correlativo || null;
          } catch (e) { /* sin correlativo */ }
        }

        // Mapear detalles con precios para la comanda de caja
        const detallesParaImprimir = cuenta.detalles.map(det => ({
          cantidad: det.Cantidad,
          nombre_platillo: det.nombre_platillo,
          precio_unitario: det.PrecioUnitario,
          subtotal: det.Subtotal,
          opciones: det.opciones || [],
        }));

        const pedidoParaImprimir = { IdPedido: this.idPedido };
        const mesaInfo = { Numero: this.pedidoInfo?.mesa ?? null };
        const mesero = this.pedidoInfo?.mesero ?? null;

        imprimirComandaCaja(
          pedidoParaImprimir,
          cuenta,
          detallesParaImprimir,
          mesaInfo,
          mesero,
          correlativo,
        );
      } catch (err) {
        console.error('Error al imprimir cuenta:', err);
      }
    },
  },
  mounted() {
    const route = useRoute();
    this.idPedido = route.params.idPedido;
    this.cargarCuentas();
    this.cargarPedidoInfo();
  },
};
</script>

<style>
.pedido-card-horizontal {
  width: 100%;
  border-radius: 12px;
  transition: box-shadow 0.2s, background-color 0.3s;
}
.pedido-card-horizontal:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}
.card-body {
  padding: 1rem 1.25rem;
}
.totales-box {
  min-width: 160px;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.2rem 1.2rem 1rem 1.2rem;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  border: 1.5px solid #e0e0e0;
}
@media (max-width: 767.98px) {
  .totales-box {
    min-width: 100%;
    margin-top: 1rem;
    padding: 1rem;
  }
}
.total-item {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}
.label-subtotal {
  font-size: 1rem;
  color: #888;
  font-weight: 500;
}
.valor-subtotal {
  font-size: 1.15rem;
  color: #222; /* negro */
  font-weight: 600;
}
.label-propina {
  font-size: 1rem;
  color: #222; /* negro */
  font-weight: 500;
}
.valor-propina {
  font-size: 1.1rem;
  color: #222; /* negro */
  font-weight: 600;
}
.label-total {
  font-size: 1.1rem;
  color: #222;
  font-weight: 700;
  letter-spacing: 0.5px;
}
.valor-total {
  font-size: 1.35rem;
  color: #28a745;
  font-weight: 800;
  letter-spacing: 1px;
}
.badge {
  font-size: 0.85rem;
}
.cantidad-badge {
  background-color: #e9ecef !important;
  color: #222 !important;
  border: 1px solid #bbb;
}
.list-group-item {
  border: none;
  background: transparent;
}
.titulo-cuentas-pedido {
  /* Ajusta el tamaño y alineación vertical si es necesario */
  font-size: 1.6rem;
  line-height: 1.2;
  /* Opcional: para mejor alineación visual con el botón */
  margin-bottom: 0 !important;
}
</style>
